/**
 * HorizontalCardInfo component.
 */
import Addon from '@shared/classes/addon';
import Button from '@components/elements/buttons/ui/cardButton';
import Container from '@components/atoms/container/horizontalCardInfoContainer';
import Content from '@shared/types/enums/content';
import DateUtils from '@shared/utils/date';
import EpgEvent from '@shared/classes/epgEvent';
import Event from '@shared/classes/event';
import FlavourConfig from '@shared/config/flavours';
import Info from '@components/atoms/text/cardInfoText';
import MediaProduction from '@shared/classes/mediaProduction';
import Plan from '@shared/classes/plan';
import ProgressBar from '@components/elements/progressBar/eventProgressBar';
import Setting from '@shared/classes/setting';
import Title from '@components/atoms/text/horizontalCardTitle';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { CardButtonIcon } from '@shared/types/enums/card';
import { useMemo } from 'react';

interface ButtonData {
  color?: string;
  display?: boolean;
  icon?: CardButtonIcon;
  text?: string;
}

interface Props {
  focused: boolean;
  item: Addon | Event | Setting;
  plan: Plan | null;
  showTitle?: boolean;
  progressBar?: {
    color: string;
    percentage: number;
  };
}

export default function HorizontalCardInfo({
  focused = false,
  item,
  plan,
  showTitle,
  progressBar,
}: Props) {
  const episodeNumber =
    item instanceof MediaProduction ? item.getEpisodeNumber() : '';
  const seasonNumber =
    item instanceof MediaProduction ? item.getSeasonNumber() : '';
  const eventStartTime = DateUtils.getTime({
    customDate: (item as EpgEvent)?.date?.start,
    displaySeconds: false,
  });

  const { button, info, title } = useMemo(() => {
    let title = item?.title;
    let button: ButtonData = {};
    let hasButton = false;
    let icon;
    let info = '';
    let text = '';

    // Get card info

    switch (item.type) {
      case Content.EPG_EVENT:
        hasButton = true;
        info = `${eventStartTime}${
          eventStartTime && (episodeNumber || seasonNumber) ? ' · ' : ''
        }${seasonNumber}${seasonNumber ? ' ' : ''}${episodeNumber}`;
        break;
      case Content.MEDIA_PRODUCTION:
        if ((item as MediaProduction)?.streams) {
          hasButton = true;
        }
        info = `${seasonNumber}${
          episodeNumber && seasonNumber ? ' ' : ''
        }${episodeNumber}`;
        break;
      case Content.SEASON:
        hasButton = !!plan;
        info = seasonNumber;
        break;
      case Content.SERIES:
        hasButton = !!plan;
        title = '';
        break;
      case Content.SETTING:
        title = TranslationManager.get(item?.title);
        break;
      case Content.SHOW:
        if ((item as MediaProduction)?.streams) {
          hasButton = true;
        }
        info = episodeNumber;
        break;
    }

    // Get card button

    if (hasButton) {
      const color = plan?.colorHex;
      const display = !!(plan || focused);
      const isFutureEvent =
        ((item as Event)?.date?.start || 0) > new Date().getTime();

      if (plan) {
        icon = CardButtonIcon.PLAN;
        text = focused
          ? TranslationManager.get('MISC.CARDS.SUBSCRIBE')
          : plan?.name?.replace(FlavourConfig.appTitle, '');
      } else if (isFutureEvent) {
        icon = CardButtonIcon.INFO;
        text = TranslationManager.get('MISC.CARDS.MORE_INFO');
      } else {
        icon = CardButtonIcon.PLAY;
        text = TranslationManager.get('MISC.CARDS.VIEW_NOW');
      }
      button = {
        color,
        display,
        icon,
        text,
      };
    }

    return { button, info, title };
  }, [focused, item, plan]);

  return (
    <Container>
      <div>
        {button?.display ? (
          <Button
            color={button?.color}
            focused={focused}
            iconType={button?.icon}
          />
        ) : null}
      </div>
      <div>
        {info && !plan ? <Info>{info}</Info> : null}
        {showTitle ? <Title>{title}</Title> : null}
        {progressBar ? (
          <ProgressBar
            percentage={progressBar.percentage}
            color={progressBar?.color}
          />
        ) : null}
      </div>
    </Container>
  );
}
